import { useEffect, useState } from "react";
import { Phone, Mail, Clock, MapPin, Leaf } from "lucide-react";
import { api } from "../lib/api";
import { formatPhone, telHref } from "../lib/format";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";

export default function Contact() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  return (
    <div>
      <Seo
        title="Request a Free Cleaning Quote | Glow Green - St. Louis & Chesterfield, MO"
        description="Request a free, no-obligation cleaning quote from Glow Green. Commercial and residential cleaning in St. Louis, Chesterfield, and surrounding Missouri communities."
      />
      <section className="bg-forest grain relative pt-32 lg:pt-40 pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf">Free Quote</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Tell us about<br />your space.
          </h1>
          <p className="mt-5 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
            Two minutes of details, one tailored estimate. Every quote is built around your space, your schedule, and your priorities - with everything included, in writing.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <QuoteForm />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-2xl bg-forest text-white p-8 grain relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="font-display text-xl font-bold">What happens next</h2>
                <ol className="mt-5 space-y-4 text-sm">
                  {[
                    "We review your details and reach out personally.",
                    "We confirm scope, frequency, and walkthrough needs.",
                    "You get a clear written estimate - everything included, nothing hidden.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-leaf text-forest-deep font-display font-bold text-xs flex items-center justify-center">{i + 1}</span>
                      <span className="text-white/80 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 pt-6 border-t border-white/10 space-y-3 text-sm">
                  {settings?.phone && (
                    <p className="flex items-center gap-3 text-white/80"><Phone className="w-4 h-4 text-leaf flex-shrink-0" /><a href={telHref(settings.phone)} data-testid="contact-phone-link" className="hover:text-leaf transition-colors">{formatPhone(settings.phone)}</a></p>
                  )}
                  {settings?.email && (
                    <p className="flex items-center gap-3 text-white/80"><Mail className="w-4 h-4 text-leaf flex-shrink-0" /><a href={`mailto:${settings.email}`} className="hover:text-leaf transition-colors">{settings.email}</a></p>
                  )}
                  {settings?.hours && (
                    <p className="flex items-center gap-3 text-white/80"><Clock className="w-4 h-4 text-leaf flex-shrink-0" />{settings.hours}</p>
                  )}
                  {settings?.address && (
                    <p className="flex items-center gap-3 text-white/80" data-testid="contact-address"><MapPin className="w-4 h-4 text-leaf flex-shrink-0" />{settings.address}</p>
                  )}
                  <p className="flex items-center gap-3 text-white/80">
                    <Leaf className="w-4 h-4 text-leaf flex-shrink-0" />
                    {settings?.service_area_note || "St. Louis, Chesterfield & surrounding Missouri communities"}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
